/**
 * BearbeitungsMaske - one Eingang and every task it produced, on one screen.
 *
 * Layout is 8.6: T1 left (~38%, constant), T2/T3/T4 stacked right, T5 as an overlay.
 * A page and not an AufgabePeek extension - the peek's 440px rail leaves T1 at ~255px,
 * too little for a mail, its files and an inline PDF.
 *
 * Lives in features/posteingang/ with the list it opens from. Task-domain helpers
 * (useTaskSplitView, aufgaben-api, the task mock) stay in features/aufgaben/.
 * T1/T3/T4 own a component each; this file is the layout and the selection.
 *
 * Missing, each waiting on a contract:
 *   - adding a note in T5 - there is no activity POST route.
 *
 * [Source: docs/eingangsverarbeitung-epic.md §3 WS-C]
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Clock, History, Paperclip, ScrollText, ShieldCheck, X } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { DataTable } from '../../components/data/DataTable';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { StatusDot } from '../../components/ui/StatusDot';
import { cn } from '../../lib/cn';
import { qk } from '../../lib/query-keys';
import {
  InlineDocumentViewer,
  isInlinePreviewable,
} from '../documents/components/InlineDocumentViewer';
import { mockDocumentUrl } from '../aufgaben/mock';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { useEingangAudit } from './hooks/useEingangAudit';
import { useEingangStatus } from './hooks/useEingangStatus';
import { useEingangActivity } from './hooks/useEingangActivity';
import { AktivitaetenHistorie } from './components/AktivitaetenHistorie';
import { SenderPrivateButton } from '../blocklist/SenderPrivateButton';
import { EingangStatusSelect } from './components/EingangStatusSelect';
import {
  FreigabeCockpitBanner,
  FreigabeCockpitButton,
  FreigabeCockpitPanel,
} from './components/FreigabeCockpit';
import { useEingangComm } from './hooks/useEingangComm';
import { useEingangTasks } from './hooks/useEingangTasks';
import { useEingangPolicenkontrolle } from './hooks/useEingangPolicenkontrolle';
import { useTaskSplitView } from '../aufgaben/hooks/useTaskSplitView';
import { statusLabel, STATUS_TONE, processLabel } from '../aufgaben/api/aufgaben-api';
import { resolveTaskRoute } from '../aufgaben/task-routes';
import {
  useEingangAttachments,
  usePatchTaskAttachment,
  useTaskAttachments,
} from './hooks/useEingangAttachments';
import { useSaveTaskDraft, useTaskDraft } from './hooks/useTaskDraft';
import { useApproveTask } from './hooks/useApproveTask';
import { resolveAssistKonfidenz } from '../communication-v2/api/comm-assist-api';
import { KonfidenzBadge } from '../communication-v2/components/konfidenz';
import { AntwortVorschlag } from './components/AntwortVorschlag';
import { EingangMailView } from './components/EingangMailView';
import { ReducedTierOverlay } from '../communication-v2/components/ReducedTierOverlay';
import {
  participantView,
  useIssueAccessRequest,
  useRequestsForComm,
} from '../communication-v2/hooks/useFreigabe';
import { freigabeRequestsMock } from './mock';
import { VersandAnhaenge } from './components/VersandAnhaenge';
import {
  GesamtfreigabeDialog,
  type DeaktivierterAnhang,
} from './components/GesamtfreigabeDialog';
import { useCommAssist } from '../communication-v2/hooks/useCommAssist';
import { useEingangCommContent } from '../communication-v2/hooks/useEingangMails';
import { dateTimeShortDe, fileSize } from '../../lib/format';
import type { TaskRow } from '../aufgaben/types';
import type { AttachmentView, ReleaseWarningItem, PosteingangPolicenkontrolleEintrag } from '@brokly/shared';
import { PK_SYSTEMANALYSE_TITLE, buildPkRueckmeldungText } from '@brokly/shared';
import { PolicenkontrolleBefundCard } from '../placements/PolicenkontrolleBefundCard';
import { PkRueckmeldungDialog } from '../placements/PkRueckmeldungDialog';
import { PkKlaerenDialog } from '../placements/PkKlaerenDialog';
import { useRueckmeldungVersichererAbschluss, useAbweichungGeklaertAbschluss } from '../placements/usePlacementAbschluss';
import { apiClient, ApiError } from '../../lib/api-client';
import { isLiveData } from '../../lib/data-mode';

/** A task counts toward the footer once it is in a terminal state. */
function isErledigt(task: TaskRow): boolean {
  return task.status === 'done' || task.status === 'cancelled';
}

/** 404 = this Aufgabe cannot have a draft; 502 = the KI service is down. Different problems. */
function draftErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 502 || error.status === 503) {
      return 'Der KI-Dienst ist zurzeit nicht erreichbar.';
    }
    if (error.status === 404) {
      return 'Für diese Aufgabe ist kein Vorschlag möglich.';
    }
  }
  return 'Vorschlag konnte nicht erzeugt werden.';
}

/** E1: one approval was staged. The status stays put, so this is the only marker. */
function isFreigegeben(task: TaskRow): boolean {
  return task.approval?.staged === true;
}

function Pane({
  title,
  action,
  children,
  footer,
  bodyClassName,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  /** Rendered below the scroll area, so a pinned control never scrolls out of reach. */
  footer?: ReactNode;
  bodyClassName?: string;
}) {
  return (
    <section className="flex min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2">
        <h2 className="flex-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h2>
        {action}
      </div>
      <div className={cn('min-h-0 flex-1 overflow-y-auto p-4', bodyClassName)}>{children}</div>
      {footer && <div className="border-t border-border p-3">{footer}</div>}
    </section>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

/**
 * One Systemanalyse entry (P2 hardening 05): the Policenkontrolle-Befund of one police this Eingang
 * produced, plus the broker's two actions. Each entry owns its own mutation hooks + dialogs, keyed by
 * its placement_id, so a (rare) Sammel-PDF with two controlled policies renders two independent cards.
 */
function SystemanalysePaneEntry({
  entry,
  commId,
  canWrite,
  canSend,
  onHinweis,
  onOpenAusschreibung,
  onUebernehmen,
}: {
  entry: PosteingangPolicenkontrolleEintrag;
  commId: string;
  canWrite: boolean;
  canSend: boolean;
  onHinweis: (text: string, ton?: 'info' | 'alert') => void;
  onOpenAusschreibung: (placementId: string) => void;
  onUebernehmen?: (text: string) => void;
}) {
  const rueckmelden = useRueckmeldungVersichererAbschluss(entry.placement_id, commId);
  const klaeren = useAbweichungGeklaertAbschluss(entry.placement_id);
  const [rueckmeldenOpen, setRueckmeldenOpen] = useState(false);
  const [klaerenOpen, setKlaerenOpen] = useState(false);

  const defaultText = buildPkRueckmeldungText({
    versicherer_name: entry.carrier_name ?? 'Versicherer',
    kunde_name: 'den Kunden',
    policy_number: entry.policy_number,
    submission_number: null,
    befund: entry.kontrolle.befund,
  });

  return (
    <div className="space-y-2">
      <PolicenkontrolleBefundCard
        kontrolle={entry.kontrolle}
        placementNumber={entry.placement_number}
        variant="pane"
        aktionen={entry.aktionen}
        canWrite={canWrite}
        canSend={canSend}
        onRueckmeldung={() => setRueckmeldenOpen(true)}
        onKlaeren={() => setKlaerenOpen(true)}
        onOpenAusschreibung={() => onOpenAusschreibung(entry.placement_id)}
      />
      {onUebernehmen && (
        <Button size="sm" variant="ghost" onClick={() => onUebernehmen(defaultText)} data-testid="pk-pane-uebernehmen">
          In Antwortentwurf uebernehmen
        </Button>
      )}
      <PkRueckmeldungDialog
        open={rueckmeldenOpen}
        onClose={() => setRueckmeldenOpen(false)}
        saving={rueckmelden.isPending}
        error={rueckmelden.error as { code?: string } | null}
        defaultTo={entry.empfaenger_vorschlag}
        defaultText={defaultText}
        bereitsGesendet={!!entry.kontrolle.rueckmeldung}
        onSend={({ to, text }) =>
          rueckmelden.mutate(
            { to, text, in_reply_to_communication_id: commId },
            { onSuccess: () => { setRueckmeldenOpen(false); onHinweis('Rueckmeldung an den Versicherer gesendet'); } },
          )
        }
      />
      <PkKlaerenDialog
        open={klaerenOpen}
        onClose={() => setKlaerenOpen(false)}
        saving={klaeren.isPending}
        error={klaeren.error as { code?: string } | null}
        onKlaeren={(grund) => klaeren.mutate({ grund }, { onSuccess: () => setKlaerenOpen(false) })}
      />
    </div>
  );
}

/**
 * A TEIL-5 panel: slides over the Maske from the right (4.6, 8.3 - "Overlay ueber
 * T2/T3 via Button, nicht eine fixe rechte Spalte"). Shared by the Aktivitätenhistory
 * and the Audit-Log so the two are the same object to a broker, differing only in what
 * they list.
 */
function Overlay({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="absolute inset-0 z-20 flex justify-end bg-black/20"
      role="dialog"
      aria-label={title}
    >
      <div className="flex h-full w-[440px] max-w-[88vw] flex-col border-l border-border bg-card shadow-xl">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <h2 className="flex-1 text-sm font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={`${title} schliessen`}
            className="grid size-7 place-items-center rounded hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}

/**
 * TEIL 2's columns: Betreff · Konfidenz · Typ · Status.
 *
 * A table, not a stack of cards: an Eingang's tasks are SIBLINGS a broker compares
 * before deciding which to work - "which one is the KI unsure about", "which is still
 * offen" - and that comparison only works when the four facts line up in columns. Same
 * `DataTable` as the Eingangsliste and the Aufgaben queue, so a task row reads the same
 * wherever it appears.
 *
 * 8.6 asks for "Aufgabenkarten mit Status-Pill, Confidence-Badge und
 * Einzelfreigabe-Button" - all three are here.
 */
function makeTaskColumns(
  onApprove: (taskId: string) => void,
  approvingId: string | null,
  onOpenTask: (task: TaskRow) => void,
  /** The anchor task's review_note (task.meta.review_note), when the split view carries
   *  one. Only ever known for the anchor task (Teil 1's split view), so it is shown on
   *  that ONE row, never invented for its siblings. */
  reviewNote: { taskId: string; note: string } | null,
): ColumnDef<TaskRow>[] {
  return [
    {
      accessorKey: 'title',
      header: 'Betreff',
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          {/* The title opens the Aufgabe itself; the rest of the row selects it for T3/T4.
              TableCell is `whitespace-nowrap`, so this opts out and truncates rather than
              forcing the pane to scroll sideways. */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenTask(row.original);
            }}
            title="Aufgabe öffnen"
            className="line-clamp-1 max-w-[18rem] whitespace-normal text-left font-medium text-foreground hover:text-primary hover:underline"
          >
            {row.original.title ?? '(ohne Titel)'}
          </button>
          {reviewNote && reviewNote.taskId === row.original.id && (
            <span className="line-clamp-2 max-w-[18rem] whitespace-normal text-xs text-muted-foreground">
              {reviewNote.note}
            </span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'confidence',
      header: 'Konfidenz',
      cell: ({ row }) => {
        // The same 0..1 wire value T3 shows, as a percent. Null is "no score", which is
        // NOT a score of zero - it renders as absent, never as 0 %.
        const konfidenz =
          row.original.confidence === null
            ? null
            : Math.round(Number(row.original.confidence) * 100);
        return konfidenz === null ? (
          <span className="text-muted-foreground">-</span>
        ) : (
          <KonfidenzBadge value={konfidenz} />
        );
      },
    },
    {
      accessorKey: 'process',
      header: 'Typ',
      cell: ({ row }) => <Badge variant="outline">{processLabel(row.original.process)}</Badge>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusDot tone={STATUS_TONE[row.original.status]} label={statusLabel(row.original.status)} />
      ),
    },
    {
      id: 'freigabe',
      header: 'Freigabe',
      cell: ({ row }) => {
        const task = row.original;
        if (isFreigegeben(task)) {
          return (
            <Badge variant="success" className="gap-1">
              <ShieldCheck className="size-3" />
              Freigegeben
            </Badge>
          );
        }
        // A terminal task was never up for approval; the Gesamtfreigabe counts it anyway.
        if (isErledigt(task)) return <span className="text-muted-foreground">-</span>;
        return (
          <Button
            size="sm"
            variant="outline"
            disabled={approvingId === task.id}
            // The row click selects the task; approving must not also move the selection.
            onClick={(e) => {
              e.stopPropagation();
              onApprove(task.id);
            }}
          >
            {approvingId === task.id ? 'Wird freigegeben …' : 'Freigeben'}
          </Button>
        );
      },
    },
  ];
}

/** audit_log.actor_kind labels — the AuditLogPage wording, so one event reads the same
 *  in the admin log and here. */
const AUDIT_ACTOR_LABELS: Record<string, string> = {
  human: 'Person',
  system: 'System',
  ai: 'KI',
};

/**
 * One attachment row. Used by BOTH T1 and T4 - and that is not a placeholder: 4.5 and
 * Bereich 3 put ALL of the Eingang's files in T4 "unabhaengig davon, welcher Aufgabe sie
 * primaer zugeordnet sind". `task_attachment` (A1/F1) adds the per-task Versandkanal-Chip
 * and the Deaktivieren-Umschalter to the T4 copy; it does NOT filter the list.
 *
 * Only a PDF is clickable: anything else has no preview, so a click would do nothing.
 */
function AttachmentRow({
  attachment,
  active,
  onOpen,
}: {
  attachment: AttachmentView;
  active?: boolean;
  onOpen: (id: string) => void;
}) {
  const previewable = isInlinePreviewable(attachment.mime_type);
  return (
    <li>
      <button
        type="button"
        disabled={!previewable}
        onClick={() => onOpen(attachment.id)}
        aria-pressed={previewable ? active === true : undefined}
        title={previewable ? 'Vorschau öffnen' : 'Für diesen Dateityp gibt es keine Vorschau'}
        className="flex w-full items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-left text-sm transition-colors enabled:hover:bg-accent disabled:cursor-default disabled:text-muted-foreground aria-[pressed=true]:border-primary aria-[pressed=true]:bg-accent"
      >
        <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate">{attachment.filename}</span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {fileSize(attachment.size_bytes)}
        </span>
      </button>
    </li>
  );
}

interface BearbeitungsMaskeProps {
  /** The Eingang being worked - a `communication` id. */
  commId: string;
  /** The task selected in T2. Null selects the first once the list arrives. */
  selectedTaskId: string | null;
  onSelectTask: (taskId: string) => void;
  onClose?: () => void;
  /**
   * The Vollzug of the Gesamtfreigabe (epic E2). Absent today - no release route
   * exists - and the footer says so rather than faking a success.
   */
  onGesamtfreigabe?: () => void;
}

export function BearbeitungsMaske({
  commId,
  selectedTaskId,
  onSelectTask,
  onClose,
  onGesamtfreigabe,
}: BearbeitungsMaskeProps) {
  const { data: tasks, isLoading: tasksLoading } = useEingangTasks(commId);
  const { data: eingangStatus } = useEingangStatus(commId);
  const rows = tasks?.rows ?? [];

  // The selection falls back to the first row so the right column is never blank
  // while a URL without ?task= is being resolved.
  const selected = rows.find((r) => r.id === selectedTaskId) ?? rows[0] ?? null;
  /* The split view is an EINGANG-level read, so it is anchored to the Eingang's FIRST
     task and NOT to the selection. Everything it supplies is shared by every sibling:
     the communication is one mail, the attachments are resolved per COMMUNICATION
     server-side (document.linked_type='communication'), and T5's history is the
     Eingang's, independent of which task is open.

     Keying it on `selected` re-fetched all three on every click — a network round-trip
     per task in live mode, and three render passes instead of one — to redraw the same
     mail and the same file list. 4.7's "Teil 1 ist konstant" is now literally true: T1
     does not depend on the selection at all.

     `anchorTask` is passed through for the mock leg: these rows come from the Posteingang
     fixture, which the hook's AUFGABEN_MOCK lookup cannot find (it would fall back to an
     unrelated task and paint the wrong mail into Teil 1). */
  const anchorTask = rows[0] ?? null;
  const { data: split } = useTaskSplitView(anchorTask?.id ?? null, anchorTask);

  const [previewId, setPreviewId] = useState<string | null>(null);
  /** S2: the inline preview is "expanded" into a Gmail-style centered pop-up. */
  const [expanded, setExpanded] = useState(false);
  /** S3: an on-demand KI-Vorschlag request is in flight. */
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const live = isLiveData();
  const [overlay, setOverlay] = useState<'aktivitaeten' | 'audit' | 'freigaben' | null>(null);
  const [warnung, setWarnung] = useState<DeaktivierterAnhang[] | null>(null);
  const [freigebend, setFreigebend] = useState(false);
  /* The footer's transient hint, shared by both Freigaben. `ton` picks the colour: a
     failure must not render in the "decide" colour a success uses. */
  const [hinweis, setHinweis] = useState<{ text: string; ton: 'info' | 'alert' } | null>(null);
  const hinweisTimer = useRef<number | null>(null);
  /* One place owns the timer: the old inline setTimeout also fired on the warning path,
     stacked on back-to-back calls (an early timer clearing a later message) and outlived
     the Maske. Precedent for clearing it: FeedbackBanner in MailReadingPane. */
  const zeigeHinweis = useCallback((text: string, ton: 'info' | 'alert' = 'info') => {
    setHinweis({ text, ton });
    if (hinweisTimer.current !== null) window.clearTimeout(hinweisTimer.current);
    hinweisTimer.current = window.setTimeout(() => setHinweis(null), 5000);
  }, []);
  useEffect(
    () => () => {
      if (hinweisTimer.current !== null) window.clearTimeout(hinweisTimer.current);
    },
    [],
  );

  /* Teil 1's Eingang. Normally it rides the task split view (one request for the whole
     left column). An Eingang with NO task has no split view at all - 8.1's unrouted
     upload - so the fallback reads the communication directly, and only then. */
  const { data: commFallback } = useEingangComm(commId, !tasksLoading && rows.length === 0);
  /* Teil 5's second panel. `audit.read` is admin-gated, so a broker without it never
     sees the button and never fires the query; the hook is enabled only while the panel
     is actually open, so opening the Maske costs no audit requests at all. */
  const permissions = useEffectivePermissions();
  const canReadAudit = permissions.can('audit.read');
  const canWriteCrm = permissions.can('crm.write');
  const audit = useEingangAudit(
    commId,
    rows.map((r) => r.id),
    canReadAudit && overlay === 'audit',
  );

  // Memoised, the AufgabenScreen precedent: `columnDef.cell` is an inline component, so
  // a fresh columns array each render is a fresh component TYPE and React remounts every
  // cell. Besides the waste, it detaches nodes mid-interaction.
  const approveTask = useApproveTask(commId);
  // `mutate` is reference-stable; the mutation OBJECT is not, so depending on it would
  // rebuild the columns on every render - the remount hazard the note above describes.
  const approveMutate = approveTask.mutate;
  /* A refused Einzelfreigabe (409 task_not_approvable, or any network failure) has to say
     so somewhere: the button alone would just re-enable itself. Both deps are stable, so
     the columns memo below still holds. */
  const approve = useCallback(
    (taskId: string) =>
      approveMutate(taskId, {
        onError: (err) =>
          zeigeHinweis(err instanceof ApiError ? err.message : 'Freigabe fehlgeschlagen.', 'alert'),
      }),
    [approveMutate, zeigeHinweis],
  );
  const approvingId = approveTask.isPending ? (approveTask.variables ?? null) : null;
  /**
   * The title opens the Aufgabe: a routable process goes to its own screen (the queue's
   * own contract), everything else to /aufgaben?task=, which opens that task's peek.
   */
  const openTask = useCallback(
    (task: TaskRow) => {
      const target = resolveTaskRoute(task);
      if (target) {
        void navigate(target.to === '/rechnungen' ? target : { to: target.to });
        return;
      }
      void navigate({ to: '/aufgaben', search: { task: task.id } });
    },
    [navigate],
  );
  // The anchor task's review_note (task.meta.review_note), only ever known for that
  // ONE task (the split view is Eingang-anchored, not per-selection; see the note at
  // `anchorTask` above), so it is shown on that row, never invented for its siblings.
  const reviewNote = useMemo(
    () =>
      anchorTask && split?.task.review_note
        ? { taskId: anchorTask.id, note: split.task.review_note }
        : null,
    [anchorTask, split?.task.review_note],
  );
  const taskColumns = useMemo(
    () => makeTaskColumns(approve, approvingId, openTask, reviewNote),
    [approve, approvingId, openTask, reviewNote],
  );

  const comm = split?.communication ?? commFallback ?? null;
  const tier = split?.your_effective_tier ?? commFallback?.your_effective_tier ?? null;
  /* D2: the two reduced tiers are the ones that may ask. 'full' needs nothing and a null
     tier is "not loaded yet", never "reduced" - offering a request there would flash a
     lock on a mail the viewer can read. */
  const reduced = tier === 'title' || tier === 'metadata';
  /* The genesis intake decision for this Eingang's sender, independent of which task is
     selected (GET /communications/:commId/content sends it at tier 'full' only, so a
     reduced-tier viewer gets none of it, never a stale copy). Same hook the Kommunikation
     reading pane uses. */
  const { data: commContent } = useEingangCommContent(commId);
  /* Being unconfirmed is CURRENT STATE, read off the api's `unconfirmed` marker (the open
     rule, not the column alone), never off `genesis.outcome`: the outcome is the intake
     decision, written once, and it still says 'unconfirmed' long after a broker confirmed
     the record - which would offer "Als Kunde übernehmen" on a client forever and
     contradict the Posteingang row. The marker also carries the person/firma SUBJECT the
     confirm and decline routes address; the anchor task cannot, its subject is NULL on the
     mail task intake opens. */
  const unconfirmedRef = commContent?.unconfirmed ?? null;
  const unconfirmedSubjectId = unconfirmedRef?.subject_id ?? null;
  /* Local override: the api's marker only flips once the refetched content lands, and the
     buttons must go the moment the broker resolved the record in THIS session. */
  const [unconfirmedResolved, setUnconfirmedResolved] = useState(false);
  const isUnconfirmed = !unconfirmedResolved && unconfirmedRef !== null;
  const unconfirmedName = isUnconfirmed ? unconfirmedRef.name : null;

  const confirmCustomer = useMutation<void, Error, string>({
    mutationFn: (subjectId) => apiClient.customers.confirmCustomer(subjectId),
    onSuccess: () => {
      setUnconfirmedResolved(true);
      zeigeHinweis('Als Kunde übernommen.');
      // Both the Kunden-grid (the new customer's status/badge) and the Posteingang
      // list (the Unbestätigt badge disappearing) read this Eingang's subject, the
      // useCreateCustomer/useCustomerStatusMutation ['customers'] prefix precedent. The
      // mail's content carries the marker: a cached copy would offer the buttons again.
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['posteingang'] });
      void qc.invalidateQueries({ queryKey: qk.commContent(commId) });
    },
    onError: (err) =>
      zeigeHinweis(
        err instanceof ApiError ? err.message : 'Kunde konnte nicht übernommen werden.',
        'alert',
      ),
  });
  const declineCustomer = useMutation<void, Error, string>({
    mutationFn: (subjectId) => apiClient.customers.declineCustomer(subjectId),
    onSuccess: () => {
      setUnconfirmedResolved(true);
      zeigeHinweis('Als «Kein Kunde» markiert.');
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['posteingang'] });
      void qc.invalidateQueries({ queryKey: qk.commContent(commId) });
    },
    onError: (err) =>
      zeigeHinweis(
        err instanceof ApiError ? err.message : 'Konnte nicht als «Kein Kunde» markiert werden.',
        'alert',
      ),
  });

  // One list for both: own requests on a reduced tier, all with names for a participant.
  const { data: commRequests } = useRequestsForComm(commId, true, freigabeRequestsMock);
  const openRequest = reduced ? (commRequests?.find((r) => r.status === 'pending') ?? null) : null;
  const issueRequest = useIssueAccessRequest((msg) => zeigeHinweis(msg, 'alert'));
  // Freigabe-Cockpit: only for participants, who get the names.
  const cockpit = !reduced && participantView(commRequests) ? (commRequests ?? []) : [];
  /* One mail per Maske, so a plain string - the pane needs a per-mail map, this does not.
     Cleared on success so the next restricted Eingang starts empty. */
  const [freigabeGrund, setFreigabeGrund] = useState('');
  /* Teil 1 reads the Eingang's own files: the split view has none for a task-free
     Eingang. Teil 4 reads the selected task's Versandanhaenge, which carry the channel
     and the on/off state. */
  const attachments = useEingangAttachments(commId).data ?? [];
  const versandRows = useTaskAttachments(selected?.id ?? null, commId).data ?? [];
  const patchAttachment = usePatchTaskAttachment(selected?.id ?? null);
  /* T5's history is the EINGANG's (all its tasks), loaded only while the panel is open. */
  const { data: activity = [], isLoading: activityLoading } = useEingangActivity(
    commId,
    overlay === 'aktivitaeten',
  );
  /* P2 hardening 05: the Policenkontrolle-Befund(e) of the police(s) this Eingang produced. The
     Systemanalyse pane appears between T2 and T3 only when there is at least one. */
  const { data: pkData } = useEingangPolicenkontrolle(commId);
  const pkEntries = pkData?.data ?? [];
  const assist = useCommAssist(comm?.id ?? null, null);
  /* S3: the draft is per Aufgabe, not per Eingang. The comm assist still supplies the
     Zusammenfassung, and its draft is the mock-mode fallback. */
  const draftQuery = useTaskDraft(selected?.id ?? null);
  const saveDraft = useSaveTaskDraft(selected?.id ?? null);
  // Live: show ONLY the per-task draft (GET /tasks/:id/draft). The per-communication assist
  // draft is the shared-across-tasks value S3 removed, so it must never fill T3 in live while
  // the task draft loads or 404s. Mock has no per-task endpoint, so it still derives from assist.
  const draftText = draftQuery.data?.draft_text ?? (live ? null : assist?.draft ?? null);

  /**
   * S3: no draft yet -> read it again. GET /tasks/:id/draft generates on a miss, so the
   * button is a retry of the same read: a 404 means the assist refused this task, and
   * saying so beats a silent no-op.
   */
  async function handleGenerateDraft() {
    if (!selected?.id || generating) return;
    setGenerating(true);
    setGenError(null);
    try {
      const result = await draftQuery.refetch();
      // refetch() resolves with the failure rather than throwing, so read result.error.
      if (!result.data) setGenError(draftErrorText(result.error));
    } catch (err) {
      setGenError(draftErrorText(err));
    } finally {
      setGenerating(false);
    }
  }

  /**
   * 8.4 + E2: the Gesamtfreigabe. The attachment check is the SERVER's - a release with
   * files a person switched off answers 409 release_attachments_warning and lists them,
   * so the dialog shows what actually blocked the release and the repeat call carries the
   * acknowledgement. A client-side check would warn about the mail's own inline pictures,
   * which the seed switches off and nobody chose to withhold.
   */
  async function gesamtfreigabe(acknowledged = false) {
    if (!commId || freigebend) return;
    // Unlike the Einzelfreigabe there is nothing to simulate here: a release executes the
    // staged actions and sends mail. Mock mode says so instead of failing at the network.
    if (!live) {
      zeigeHinweis('Die Gesamtfreigabe ist nur mit verbundenem Backend verfügbar.');
      return;
    }
    setFreigebend(true);
    setWarnung(null);
    try {
      const result = await apiClient.posteingang.release(commId, {
        attachments_warning_acknowledged: acknowledged,
      });
      onGesamtfreigabe?.();
      zeigeHinweis(
        result.sends_queued > 0
          ? `Eingang freigegeben, ${result.sends_queued} Antwort(en) im Versand.`
          : 'Eingang freigegeben.',
      );
      void qc.invalidateQueries({ queryKey: qk.inbox({ eingang: true, comm_id: commId }) });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'release_attachments_warning') {
        setWarnung(
          (err.data as ReleaseWarningItem[] | undefined)?.map((item) => ({
            taskId: item.task_id,
            taskTitle: item.task_title ?? '(ohne Titel)',
            attachmentId: item.document_id,
            filename: item.filename,
          })) ?? [],
        );
        return;
      }
      zeigeHinweis(
        err instanceof ApiError ? err.message : 'Gesamtfreigabe fehlgeschlagen.',
        'alert',
      );
    } finally {
      setFreigebend(false);
    }
  }

  const preview = attachments.find((a) => a.id === previewId) ?? null;

  /* Esc closes the topmost layer. The preview's × has always PROMISED «(Esc)» and
     nothing implemented it; with the preview now covering all of Teil 1 that shortcut
     is the fast way back to the mail, so it had to become real. The Gesamtfreigabe
     dialog owns Esc while it is open (its own handler) - this must not close two
     layers on one keypress. */
  useEffect(() => {
    if (warnung !== null) return;
    if (overlay === null && previewId === null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      // S2: Esc peels the topmost layer — the pop-up first, then the overlay, then the preview.
      if (expanded) setExpanded(false);
      else if (overlay !== null) setOverlay(null);
      else setPreviewId(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [warnung, overlay, previewId, expanded]);
  /** Clicking the row that is already open closes it - 4.2: "erneuter Klick oder «×»".
   *  Opening/closing a preview always drops the expanded pop-up (S2). */
  const togglePreview = (id: string) => {
    setExpanded(false);
    setPreviewId((cur) => (cur === id ? null : id));
  };
  /* The release requires every task done or staged, so the footer counts the same thing
     rather than "erledigt" alone - the old wording predates the staged state. */
  const erledigt = rows.filter((t) => isErledigt(t) || isFreigegeben(t)).length;

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      {/* ── Kopf: which Eingang is being worked ── */}
      <header className="flex items-center gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-sm font-semibold text-foreground">
            {comm?.subject ?? 'Eingang'}
          </h1>
          <p className="truncate text-xs text-muted-foreground">
            {rows.length === 1 ? '1 Aufgabe' : `${rows.length} Aufgaben`}
          </p>
        </div>
        {/* Eingang status, the same control as the table's Status column. */}
        {cockpit.length > 0 && (
          <FreigabeCockpitButton requests={cockpit} onOpen={() => setOverlay('freigaben')} />
        )}
        {eingangStatus !== undefined && (
          <EingangStatusSelect commId={commId} status={eingangStatus} />
        )}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Eingang schliessen"
            title="Eingang schliessen"
            className="grid size-7 shrink-0 place-items-center rounded hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        )}
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[38%_1fr]">
        {/* ── T1: the Eingang, constant while the right column follows the selection ──
            8.6: "Kopfbereich mit Eingangsmetadaten (Kanal, Absender/Quelle, Zeitpunkt),
            darunter Anhangsliste mit Klick-zu-oeffnen/-schliessen Inline-Vorschau". */}
        <div className="flex min-h-0 flex-col border-border lg:border-r">
          <div className="flex items-center gap-2 border-b border-border px-4 py-2">
            <h2 className="flex-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Eingang
            </h2>
            {/* T5 lives behind a button in TEIL 1 (8 Layoutprinzip, 4.6), not in the task
                column - it is history of the whole Eingang, independent of the selection.
                Two panels, two buttons side by side: the Aktivitätenhistory is the work
                log a broker reads, the Audit-Log is the append-only compliance record
                (J1). Same overlay, different content - see `Overlay`. */}
            <button
              type="button"
              onClick={() => setOverlay('aktivitaeten')}
              className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
            >
              <History className="size-3.5" />
              Aktivitätenhistory
            </button>
            {/* `audit.read` is admin-gated (D74-ADMIN-IS-AUDITOR), so for most brokers
                this button is simply not there - better than one that always 403s. */}
            {canReadAudit && (
              <button
                type="button"
                onClick={() => setOverlay('audit')}
                className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
              >
                <ScrollText className="size-3.5" />
                Audit-Log
              </button>
            )}
          </div>

          {/* An open preview takes the WHOLE Teil-1 area (epic C2 AC: "full T1 area"),
              it does not share it with the mail. A 38%-wide column split again in two
              gave a 2-3 MB Police about 480px of height - a page you squint at rather
              than read, which is the one thing an inline viewer has to get right.
              Nothing is stranded by that: the file list is ALSO in Teil 4, so switching
              from Police.pdf to Rechnung.pdf needs no detour back through here, and the
              viewer's own × (or Esc) restores the mail. 4.2 asks for no SEITENWECHSEL,
              which this still honours - Teil 2/3/4 never move. */}
          {!preview && (
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {/* Open Freigabe-Anfragen, named, one click into the cockpit panel. */}
              <FreigabeCockpitBanner requests={cockpit} onOpen={() => setOverlay('freigaben')} />

              {/* D2: scope for the customer but not for this mail's content - the panel
                  takes the BODY's place (the Kommunikation tab does the same), so the
                  sender, channel and date above it still show, which is the metadata level
                  D2 grants. A viewer with NO scope never reaches this screen at all: that
                  Eingang does not leave the database (D1, RLS 0113). */}
              {comm && (
                <EingangMailView
                  comm={comm}
                  tier={tier}
                  genesis={commContent?.genesis}
                  unconfirmedName={unconfirmedName}
                  bodySlot={
                    reduced ? (
                      <ReducedTierOverlay
                        tier={tier === 'metadata' ? 'metadata' : 'title'}
                        pending={Boolean(openRequest)}
                        requestedAt={openRequest?.created_at ?? null}
                        inFlight={issueRequest.isPending}
                        onRequest={(reason) =>
                          issueRequest.mutate(
                            { commId, reason },
                            {
                              onSuccess: () => {
                                setFreigabeGrund('');
                                zeigeHinweis('Freigabe angefragt.');
                              },
                            },
                          )
                        }
                        reason={freigabeGrund}
                        onReasonChange={setFreigabeGrund}
                      />
                    ) : undefined
                  }
                />
              )}

              {isUnconfirmed && canWriteCrm && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    disabled={confirmCustomer.isPending || declineCustomer.isPending}
                    onClick={() =>
                      unconfirmedSubjectId && confirmCustomer.mutate(unconfirmedSubjectId)
                    }
                  >
                    Als Kunde übernehmen
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={confirmCustomer.isPending || declineCustomer.isPending}
                    onClick={() =>
                      unconfirmedSubjectId && declineCustomer.mutate(unconfirmedSubjectId)
                    }
                  >
                    Kein Kunde
                  </Button>
                </div>
              )}

              {/* P2 hardening 04: mark this sender private for me only (soft, reversible). */}
              {comm && !reduced && (
                <SenderPrivateButton from={comm.from ?? null} channel={comm.channel ?? null} />
              )}

              {attachments.length > 0 && (
                <div className="mt-4 flex flex-col gap-1.5">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Dateien ({attachments.length})
                  </h3>
                  <ul className="flex flex-col gap-1">
                    {attachments.map((a) => (
                      <AttachmentRow
                        key={a.id}
                        attachment={a}
                        active={a.id === previewId}
                        onOpen={togglePreview}
                      />
                    ))}
                  </ul>
                </div>
              )}

              {!comm && !tasksLoading && <Empty>Kein Eingang geladen.</Empty>}
            </div>
          )}

          {/* 4.2: the preview opens INSIDE the mask ("ohne die Maske zu verlassen", "ohne
              Seitenwechsel"), which is why this is a pane in Teil 1 and not a dialog. */}
          {preview && (
            <div className="min-h-0 flex-1 p-3">
              {expanded ? (
                // S2: while the pop-up is open the inline pane steps aside, so only the
                // pop-up renders the PDF (the Blob is query-cached; this avoids a second
                // iframe/renderer for the same document).
                <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground">
                  Vorschau in Vollansicht geöffnet
                </div>
              ) : (
                <InlineDocumentViewer
                  documentId={preview.id}
                  filename={preview.filename}
                  mimeType={preview.mime_type}
                  // Ignored in live mode; in mock it is what the iframe renders, so the
                  // Teil-1 preview is real without a backend.
                  mockUrl={mockDocumentUrl(preview.id)}
                  sizeBytes={preview.size_bytes}
                  onClose={() => setPreviewId(null)}
                  onExpand={() => setExpanded(true)}
                  className="h-full"
                />
              )}
            </div>
          )}
        </div>

        {/* ── T2 / T3 / T4 stacked, "ohne Tab-Wechsel" (4.5, 8.6) ── */}
        {/* T3 gets the largest share: 8.2 wants the draft and the file list both live
            and editable without a Reiter, and T3 now holds a text EDITOR while T2 is a
            short card list. Every pane still scrolls on its own, so a long list stays
            reachable. */}
        <div
          className={cn(
            'grid min-h-0 divide-y divide-border',
            pkEntries.length > 0
              ? 'grid-rows-[minmax(0,0.7fr)_auto_minmax(0,1.2fr)_minmax(0,0.7fr)]'
              : 'grid-rows-[minmax(0,0.85fr)_minmax(0,1.3fr)_minmax(0,0.85fr)]',
          )}
        >
          <Pane title={`Aufgaben (${rows.length})`}>
            {tasksLoading ? (
              <Empty>Lade Aufgaben …</Empty>
            ) : rows.length === 0 ? (
              <Empty>Dieser Eingang hat keine Aufgaben.</Empty>
            ) : (
              // `activeRowId` is what ties T2 to T3/T4: the highlighted row IS the task
              // the two panes below are showing (4.5).
              <DataTable
                columns={taskColumns}
                data={rows}
                getRowId={(row) => row.id}
                activeRowId={selected?.id}
                onRowClick={(row) => onSelectTask(row.id)}
              />
            )}
          </Pane>

          {/* P2 hardening 05: the Systemanalyse pane - the Policenkontrolle result + the concrete
              per-field difference, between «Aufgaben» and «Antwort» (Stefan: "zwischen Aufgabe und
              Antwort"). Only when this Eingang produced a controlled police; otherwise the Maske is
              byte-identical to before. */}
          {pkEntries.length > 0 && (
            <Pane title={PK_SYSTEMANALYSE_TITLE} bodyClassName="max-h-[40%]">
              <div className="space-y-3">
                {pkEntries.map((entry) => (
                  <SystemanalysePaneEntry
                    key={entry.placement_id}
                    entry={entry}
                    commId={commId}
                    canWrite={permissions.can('placement.write')}
                    canSend={permissions.can('communication.send')}
                    onHinweis={zeigeHinweis}
                    onOpenAusschreibung={(placementId) =>
                      void navigate({ to: '/ausschreibungen/$id', params: { id: placementId }, search: { tab: 'historie' } })
                    }
                    onUebernehmen={draftQuery.data ? (text) => saveDraft.mutate(text) : undefined}
                  />
                ))}
              </div>
            </Pane>
          )}

          {/* 8.2: der AI-Antwortentwurf, mit ConfidenceBadge und als KI-Vorschlag
              gekennzeichnet - und editierbar, "ohne dass ein Wechsel zwischen Reitern
              noetig ist". The read-only `AssistenzBlock` stays the reading pane's; the
              Maske is where a broker WORKS the draft. */}
          <Pane title="Antwort-/Lösungsvorschlag">
            {/* S3: AntwortVorschlag owns every state now — draft, summary-only, and the
                no-draft case with an on-demand generate button (so it also appears when
                only a summary exists, M1). `assist` may be null (nothing yet). */}
            <AntwortVorschlag
              summary={assist?.summary ?? null}
              draft={draftText}
              originalText={draftQuery.data?.original_ai_text ?? null}
              taskId={selected?.id ?? null}
              // The assist's own percent, falling back to the task konfidenz (0..1
              // on the wire, shown as a percent) - the MailReadingPane convention.
              konfidenz={
                assist
                  ? resolveAssistKonfidenz(assist, Math.round(Number(selected?.confidence ?? 0) * 100))
                  : Math.round(Number(selected?.confidence ?? 0) * 100)
              }
              onGenerate={handleGenerateDraft}
              generating={generating}
              genError={genError}
              onSave={draftQuery.data ? (text) => saveDraft.mutate(text) : undefined}
              saving={saveDraft.isPending}
              saveError={saveDraft.isError ? 'Speichern fehlgeschlagen.' : null}
            />
          </Pane>

          {/* 8.2: alle Dateien des Eingangs, je standardmaessig aktiviert, Versandkanal
              pro Datei waehl- und uebersteuerbar, Ueberfluessiges deaktivierbar -
              direkt unter T3, ohne Reiter. */}
          <Pane title="Zugehörige Anhänge & Versandkanal">
            <VersandAnhaenge
              rows={versandRows}
              onPatch={(documentId, patch) => patchAttachment.mutate({ documentId, patch })}
              previewId={previewId}
              onOpenPreview={togglePreview}
            />
          </Pane>
        </div>
      </div>

      {/* ── Sticky Fusszeile (8.6 / epic E5) ──
          Progress plus the Gesamtfreigabe, which appears ONLY at full progress -
          8.4 starts at "nach Freigabe aller Aufgaben". A terminal task counts as
          freigegeben: the release accepts done OR staged. */}
      <footer className="flex shrink-0 items-center gap-2 border-t border-border bg-card px-4 py-2 text-xs text-muted-foreground">
        <Clock className="size-3.5" />
        <span>
          {erledigt} von {rows.length} Aufgaben freigegeben
        </span>
        {hinweis && (
          <span
            role="status"
            className={cn('truncate', hinweis.ton === 'alert' ? 'text-sem-alert' : 'text-sem-decide')}
          >
            {hinweis.text}
          </span>
        )}
        <div className="flex-1" />
        {rows.length > 0 && erledigt === rows.length && (
          <Button size="sm" disabled={freigebend} onClick={() => void gesamtfreigabe()}>
            <ShieldCheck className="size-4" />
            Gesamtfreigabe Eingang
          </Button>
        )}
      </footer>

      {/* ── 8.4: die Warnung, wenn ein Anhang deaktiviert ist ── */}
      <GesamtfreigabeDialog
        open={warnung !== null}
        anhaenge={warnung ?? []}
        onJumpToTask={(taskId) => {
          setWarnung(null);
          onSelectTask(taskId);
        }}
        onConfirm={() => void gesamtfreigabe(true)}
        onCancel={() => setWarnung(null)}
      />

      {/* ── T5: die beiden Panels über der Maske (4.6, 8.3) ── */}
      {overlay === 'aktivitaeten' && (
        <Overlay title="Aktivitätenhistory" onClose={() => setOverlay(null)}>
          {/* Sent replies are named and open the whole mail (AktivitaetenHistorie). */}
          <AktivitaetenHistorie commId={commId} entries={activity} isLoading={activityLoading} />
          {/* 4.6's Notizfunktion needs an activity POST route; none exists in the api. */}
        </Overlay>
      )}

      {overlay === 'freigaben' && (
        <Overlay title="Freigabe-Anfragen" onClose={() => setOverlay(null)}>
          <FreigabeCockpitPanel
            requests={cockpit}
            onFeedback={(m) => zeigeHinweis(m)}
            onError={(m) => zeigeHinweis(m, 'alert')}
          />
        </Overlay>
      )}

      {overlay === 'audit' && (
        <Overlay title="Audit-Log" onClose={() => setOverlay(null)}>
          {audit.isLoading ? (
            <Empty>Lade Audit-Log …</Empty>
          ) : audit.rows.length === 0 ? (
            <Empty>Keine Audit-Einträge zu diesem Eingang.</Empty>
          ) : (
            <ol className="flex flex-col gap-2 border-l border-border pl-3">
              {audit.rows.map((entry) => (
                <li key={entry.id} className="relative">
                  <span className="absolute -left-[1.1rem] top-1.5 size-1.5 rounded-full bg-border" />
                  <p className="flex flex-wrap items-center gap-1.5 text-sm text-foreground">
                    <span className="font-medium">{entry.action}</span>
                    {/* J1 badges the high ones (release, deactivate) - the entries an
                        auditor is looking FOR. */}
                    {entry.severity === 'high' && <Badge variant="warning">Hoch</Badge>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {AUDIT_ACTOR_LABELS[entry.actor_kind] ?? entry.actor_kind} ·{' '}
                    {entry.entity_type} · {dateTimeShortDe(entry.created_at)}
                  </p>
                </li>
              ))}
            </ol>
          )}
          {/* The rows are WORM (migrations 0001+0002): read-only here, by design. */}
          {audit.isError && (
            <p className="mt-3 text-xs text-sem-decide">
              Teile des Audit-Logs konnten nicht geladen werden.
            </p>
          )}
        </Overlay>
      )}

      {/* ── S2: Gmail-style centered pop-up ──
          The SAME document, big and readable, over the blurred mask. Opened via the inline
          viewer's expand control; the inline pane steps aside while it is open, so only the
          pop-up renders the PDF. Esc / backdrop / × closes it. */}
      {preview && expanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm sm:p-8"
          onClick={() => setExpanded(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Vorschau: ${preview.filename}`}
            className="flex h-full max-h-[88vh] w-full max-w-6xl"
            onClick={(e) => e.stopPropagation()}
          >
            <InlineDocumentViewer
              documentId={preview.id}
              filename={preview.filename}
              mimeType={preview.mime_type}
              mockUrl={mockDocumentUrl(preview.id)}
              sizeBytes={preview.size_bytes}
              onClose={() => setExpanded(false)}
              className="h-full w-full"
            />
          </div>
        </div>
      )}
    </div>
  );
}
